<template>
	<template v-if="data">
		<TheForm :model="data" :rules="rules" ref="formRef" :waiting="waiting" :id="formId">
			<ButtonSticky @buttonclick="saveCase()" isSubmit>
				<template v-slot:icon><saveIcon /></template>
				<template v-slot:text> Speichern </template>
			</ButtonSticky>

			<section>
				<ButtonBack />

				<el-row class="titlerow">
					<el-col :sm="12">
						<h1>{{ localTitle }}</h1>
					</el-col>
					<el-col :sm="12">
						<div :class="`has-status has-status--${$getStateColor('case', data.state)}`">
							{{ $getStateFullLabel('case', data.state, false) }}
						</div>
					</el-col>
				</el-row>

				<el-row class="button-panel">
					<TheButton type="danger" @buttonclick="handleOpenSmallSenateDialog" :disabled="data.state !== CaseState.NoStatus">
						An kl. Senat zur Prüfung
					</TheButton>
					<TheButton type="danger" @buttonclick="openDialogRequestStatement = true" :disabled="!isStatementAllowed">
						Stellungnahme anfordern
					</TheButton>
					<TheButton
						type="danger"
						@buttonclick="openDialogSendCaseWerberat = true"
						:disabled="isSentToSmallSenate(data) || isSentToWerberat(data) || hasDecision(data)"
					>
						In den WR zur Abstimmung
					</TheButton>
					<TheButton type="danger" @buttonclick="openDialogPreliminaryDecision = true" :disabled="hasDecision(data)"
						>Vorzeitige Entscheidung</TheButton
					>

					<TheButton v-if="!data.publishedDate" type="danger" @buttonclick="openDialogPublishCase = true" :disabled="!hasDecision(data)">
						Fall veröffentlichen
					</TheButton>
					<TheButton v-else type="danger" @buttonclick="openDialogPublishCase = true"> Veröffentlichung zurückziehen </TheButton>
				</el-row>

				<TheBox :title="'Fall Infos'" :hasDataGrid="true">
					<el-row>
						<el-col :sm="12" :xs="24">
							<el-form-item label="Falltitel" prop="title">
								<el-tooltip effect="dark" :content="data.title" placement="top" :disabled="data.title.length < 40">
									<el-input v-model="data.title" :placeholder="'Bitte Falltitel eingeben'" :disabled="data.publishedDate" />
								</el-tooltip>
							</el-form-item>

							<el-form-item label="Beschwerdegrund:" prop="category">
								<el-select v-if="categoryOptions" v-model="selectedCategory" :disabled="data.publishedDate">
									<el-option v-for="option in categoryOptions" :key="option.value?.number" :label="option.label" :value="option.value?.number" />
								</el-select>
							</el-form-item>

							<el-form-item label="Stn. angefragt:">
								<span>{{ data.statements?.filter((st) => st.answer).length }}</span>
							</el-form-item>

							<el-form-item label="Stn-Gruppen:">
								{{ data.statementRoles && data.statementRoles.length > 0 ? data.statementRoles?.map((role) => role.name).join(', ') : '-' }}
							</el-form-item>

							<el-form-item label="WR-Ent. bis:">
								<span>{{ data.voteEndDate ? $formatDate(data.voteEndDate) : '-' }}</span>
							</el-form-item>

							<el-form-item label="Beschwerden:">
								<span>{{ data.complaints?.length }}</span>
							</el-form-item>
						</el-col>

						<el-col :sm="12" :xs="24">
							<el-form-item label="Stn. bis:">
								<span>{{ getCaseVoteEnde(data, $formatDate) }}</span>
							</el-form-item>

							<el-form-item label="Datum:">
								<span>{{ $formatDate(data.createdDate) }}</span>
							</el-form-item>

							<el-form-item label="ID:">
								<span>{{ data.hid }}</span>
							</el-form-item>

							<el-form-item label="Rückfragen:">
								<span>{{ getCallbacksCount(data) }}</span>
							</el-form-item>

							<el-form-item label="Auf Website veröffentlicht:" prop="createdDate" v-if="data.publishedDate">
								<el-date-picker v-model="data.publishedDate" type="date" placeholder="TT.MM.JJJJ" :format="'DD.MM.YYYY'" />
							</el-form-item>

							<el-form-item label="Branche:" prop="sectors">
								<el-select v-if="sectorOptions" v-model="selectedSectors" multiple :disabled="data.publishedDate">
									<el-option v-for="option in sectorOptions" :key="option.value?.name" :label="option.label" :value="option.value?.name" />
								</el-select>
							</el-form-item>
						</el-col>
					</el-row>
				</TheBox>

				<TheBox :title="'Medium & Reichweite'" :hasDataGrid="true">
					<el-row>
						<el-col :sm="12" :xs="24">
							<el-form-item label="Medium:" prop="mediums">
								<el-select v-if="mediaOptions" v-model="selectedMediums" multiple :disabled="data.publishedDate">
									<el-option v-for="option in mediaOptions" :key="option.value?.name" :label="option.label" :value="option.value?.name" />
								</el-select>
							</el-form-item>
						</el-col>
						<el-col :sm="12" :xs="24">
							<el-form-item label="Reichweite:" prop="reaches">
								<el-select v-if="reachOptions" v-model="selectedReaches" multiple :disabled="data.publishedDate">
									<el-option v-for="option in reachOptions" :key="option.value?.name" :label="option.label" :value="option.value?.name" />
								</el-select>
							</el-form-item>
						</el-col>
					</el-row>
				</TheBox>
			</section>

			<template v-if="!isNotSentToSmallSenateYet(data)">
				<section v-if="isSentToWerberat(data) || hasDecision(data)">
					<ElCollapse :v-model="smallSenateSectionCollapsed" @change="smallSenateSectionCollapsed = !smallSenateSectionCollapsed">
						<ElCollapseItem name="smallSenate">
							<template #title>
								<h2>Entscheidungen kleiner Senat</h2>
							</template>

							<DecisionsTable
								:tableData="smallSenateDecisions"
								:totalCount="smallSenateDecisions.length"
								:caseTitle="data.title"
								:preVoteEndDate="data.preVoteEndDate ? data.preVoteEndDate : ''"
								:voteEndDate="data.voteEndDate ? data.voteEndDate : ''"
								@loadNextPage="null"
							/>

							<SectionStatistics
								:statistics="statistic"
								:type="DecisionType.SmallSenate"
								:title="data.preVoteRole?.name"
								:werberatRoles="null"
								:caseHid="data.hid"
							/>
						</ElCollapseItem>
					</ElCollapse>
				</section>
				<section v-else>
					<h2>Entscheidungen kleiner Senat</h2>
					<DecisionsTable
						:tableData="smallSenateDecisions"
						:totalCount="smallSenateDecisions.length"
						:caseTitle="data.title"
						:preVoteEndDate="data.preVoteEndDate ? data.preVoteEndDate : ''"
						:voteEndDate="data.voteEndDate ? data.voteEndDate : ''"
						@loadNextPage="null"
					/>

					<BoxSenateDecision
						v-if="isSentToSmallSenate(data) || (isInStatements(data) && !isSentToWerberat(data) && !data.publishedDate)"
						:caseId="id"
						@reload="loadCase"
						:existingDecisions="smallSenateDecisions"
						:currentState="data.state"
						:decidedDate="data.statementsDate"
					/>

					<SectionStatistics
						:statistics="statistic"
						:type="DecisionType.SmallSenate"
						:title="data.preVoteRole?.name"
						:werberatRoles="null"
						:caseHid="data.hid"
					/>
				</section>
			</template>

			<template v-if="(isSentToWerberat(data) || hasDecision(data)) && !isSmallSenateDecision(data)">
				<template v-if="data.voteRoles && data.voteRoles.length > 0">
					<section class="no-marginbottom">
						<h2>Zugewiesene Gruppen</h2>
					</section>
					<RoleSlider :roles="data.voteRoles ? data.voteRoles : []" :showDelete="false" />
				</template>

				<section>
					<h2>Werberatsentscheidungen mit Kommentar </h2>
					<DecisionsTable
						:tableData="werberatDecisions"
						:totalCount="werberatDecisions.length"
						@loadNextPage="null"
						:caseTitle="data.title"
						:preVoteEndDate="data.preVoteEndDate ? data.preVoteEndDate : ''"
						:voteEndDate="data.voteEndDate ? data.voteEndDate : ''"
					/>
				</section>

				<section>
					<TheBox v-if="hasDecision(data) && !isEditingFinalDecision" :title="'Endgültige Entscheidung:'" :id="'decision-final'">
						<template #boxheaderRight>
							<TheButton type="default" @buttonclick="isEditingFinalDecision = true">Entscheidung bearbeiten</TheButton>
						</template>

						<div :class="`has-status has-status--${$getStateColor('case', data.state)}`">
							{{ $getStateFullLabel('case', data.state, false) }}
						</div>

						<h3>Begründung:</h3>
						<div class="text">{{ data.reasonText ? data.reasonText : '-' }}</div>

						<el-row v-if="data.decidedDate" class="decided-info">
							<el-text>Entscheidung getroffen: {{ $formatDate(data.decidedDate, 'HH:mm, DD.MM.YYYY') }}</el-text>
						</el-row>
					</TheBox>
					<BoxWerberatDecision
						v-else-if="isSentToWerberat(data) || (hasDecision(data) && isEditingFinalDecision)"
						:caseId="id"
						@reload="loadCase"
						@stopEditing="isEditingFinalDecision = false"
						:isEditing="hasDecision(data) && isEditingFinalDecision"
						:existingDecisions="werberatDecisions"
						:currentFinalDecision="data.state"
						:currentReasonText="data.reasonText"
					/>
				</section>
			</template>

			<template v-else-if="isSmallSenateDecision(data)">
				<section>
					<BoxSenateDecision
						:caseId="id"
						@reload="loadCase"
						:existingDecisions="smallSenateDecisions"
						:currentState="data.state"
						:decidedDate="data.statementsDate"
						:showSmallSenatePreliminaryDecision="true"
						:reasonText="data.reasonText"
					/>
				</section>
			</template>

			<SectionStatistics
				v-if="showWerberatStatistic(data)"
				:statistics="statistic"
				:type="DecisionType.Werberat"
				:werberatRoles="data.voteRoles"
				:caseHid="data.hid"
			/>

			<section>
				<el-row>
					<el-col :sm="12" :xs="24">
						<h2>Beschwerden</h2>
					</el-col>
					<el-col :sm="12" :xs="24" class="flex flex--justifyend">
						<RouterLink
							:to="'/beschwerde-anlegen'"
							class="el-button el-button--primary is-round"
							:title="'Neue Beschwerde anlegen'"
							v-show="!data.publishedDate"
						>
							<el-icon><Plus /></el-icon>Neue Beschwerde
						</RouterLink>
					</el-col>
				</el-row>

				<ComplaintsTable
					:tableData="data.complaints ? data.complaints : []"
					:totalCount="data.complaints ? data.complaints.length : 0"
					@loadNextPage="null"
				/>

				<el-row>
					<el-col :sm="12" :xs="24">
						<h2>Stellungnahmen</h2>
					</el-col>
					<el-col :sm="12" :xs="24" class="flex flex--justifyend" v-if="isStatementAllowed">
						<RouterLink :to="'/stellungnahme-anlegen/' + id" class="el-button el-button--primary is-round" :title="'Neue Stellungnahme anlegen'">
							<el-icon><Plus /></el-icon>Neue Stellungnahme
						</RouterLink>
					</el-col>
				</el-row>

				<StatementsTable
					:tableData="data.statements ? data.statements : []"
					:totalCount="data.statements ? data.statements.length : 0"
					@loadNextPage="null"
					:shouldSeePersonalData="true"
				/>
			</section>
		</TheForm>

		<DetailsMedia
			:files="caseFiles"
			@addToFiles="addToFiles"
			@removeFromFiles="removeFromFiles"
			:allowDeleteFromServer="true"
			:showAddButton="!data.publishedDate"
			:pageType="'fall'"
		/>
		<DetailsLinks
			:links="caseLinks"
			@addToLinks="addToLinks"
			@removeFromLinks="removeFromLinks"
			:showAddButton="!data.publishedDate"
			:allowDeleteFromServer="true"
			:pageType="'fall'"
		/>

		<section>
			<TheBox :title="'Hinweis (auch für Werberäte sichtbar)'">
				<el-input
					v-model="data.externalNote"
					:autosize="{ minRows: 3 }"
					type="textarea"
					placeholder="Schreiben Sie hier Ihre Notiz. Diese Notiz ist auch für Werberäte sichtbar."
					:disabled="data.publishedDate"
				/>
			</TheBox>
			<TheBox :title="'Interne Notiz (nur für Mitarbeiter sichtbar)'">
				<el-input
					v-model="data.internalNote"
					:autosize="{ minRows: 3 }"
					type="textarea"
					placeholder="Schreiben Sie hier Ihre Notiz. Ihre Notiz ist nur für Mitarbeiter sichtbar."
					:disabled="data.publishedDate"
				/>
			</TheBox>

			<TheBox :id="'log'" :title="'Log'">
				<LogsTable :logs="logs" :loading="logsLoading" />
			</TheBox>
		</section>

		<DialogPreliminaryDecision v-model:open="openDialogPreliminaryDecision" :caseId="id" @reload="loadCase" />
		<DialogSendCaseSmallSenate
			v-model:open="openDialogSendCaseSmallSenate"
			:caseId="id"
			:nextSenateOption="nextSmallSenate"
			@close="closeDialogSendCaseSmallSenate"
		/>
		<DialogSendCaseWerberat
			v-model:open="openDialogSendCaseWerberat"
			:caseId="id"
			:nextSenateOption="nextSmallSenate"
			@close="closeDialogSendCaseWerberat"
		/>
		<DialogRequestStatement v-model:open="openDialogRequestStatement" :caseId="id" @reload="loadCase" />

		<DialogPublishCase
			v-model:open="openDialogPublishCase"
			:caseId="id"
			:shouldPublishCase="!data.publishedDate ? true : false"
			:dataMissing="selectedSectors.length === 0 || selectedReaches.length === 0 ? true : false"
			@reload="loadCase"
		/>

		<DialogLeavePage v-model:open="openClosingDialog" @cancel="leavePage" @save="saveCase(true)" />
		<DialogeLeavePageCaseErr v-model:open="openDialogeLeavePageCaseErr" @cancel="leavePage" />
	</template>
	<section v-else-if="loading === false">
		<ButtonBack />
		<el-alert :title="texts.general.emptyText" type="error" effect="dark" :closable="false" />
	</section>
</template>

<script lang="ts">
import BoxSenateDecision from '@/components/boxes/BoxSenateDecision.vue';
import BoxWerberatDecision from '@/components/boxes/BoxWerberatDecision.vue';
import DialogPreliminaryDecision from '@/components/dialogs/DialogPreliminaryDecision.vue';
import DialogPublishCase from '@/components/dialogs/DialogPublishCase.vue';
import DialogRequestStatement from '@/components/dialogs/DialogRequestStatement.vue';
import DialogSendCaseSmallSenate from '@/components/dialogs/DialogSendCaseSmallSenate.vue';
import DialogSendCaseWerberat from '@/components/dialogs/DialogSendCaseWerberat.vue';
import ComplaintsTable from '@/components/tables/ComplaintsTable.vue';
import LogsTable from '@/components/tables/LogsTable.vue';
import StatementsTable from '@/components/tables/StatementsTable.vue';
import { getCase, getCaseLogs, getCaseNextSmallSenate, getCaseStatistics, saveCase } from '@/requests/cases';
import { SaveCaseRequest } from '@/types/formdataTypes';
import { startLoading, stopLoading } from '@/utils/loading/loadingHandler';
import { Plus, Loading } from '@element-plus/icons-vue';
import type { FormInstance } from 'element-plus';
import { ElMessage, ElCollapse, ElCollapseItem } from 'element-plus';
import { defineComponent, ref, markRaw } from 'vue';
import type { RouteLocationRaw } from 'vue-router';
import saveIcon from '../assets/icons/saveIcon.vue';
import ButtonBack from '../components/buttons/ButtonBack.vue';
import ButtonSticky from '../components/buttons/ButtonSticky.vue';
import DialogLeavePage from '../components/dialogs/DialogLeavePage.vue';
import DialogeLeavePageCaseErr from '../components/dialogs/DialogeLeavePageCaseErr.vue';
import SectionStatistics from './sections/SectionStatistics.vue';
import RoleSlider from '../components/slider/RoleSlider.vue';
import DecisionsTable from '../components/tables/DecisionsTable.vue';
import TheBox from '../components/TheBox.vue';
import TheButton from '../components/TheButton.vue';
import TheForm from '../components/TheForm.vue';
import {
	HistoryEntryResponse,
	CaseResponse,
	CaseState,
	DecisionState,
	DecisionType,
	DetailFile,
	DetailLink,
	FileDto,
	LinkDto,
	SendCaseType,
	StatisticResponse,
} from '../types/types';
import { getCaseFiles } from '../utils/fileHandling/files';
import { returnFormOptions } from '../utils/formOptions.js';
import { validateForm, hasFormChanged } from '../utils/functions';
import { getCaseLinks } from '../utils/linkHandling/links';
import { texts } from '../utils/texts.js';
import DetailsLinks from './sections/DetailsLinks.vue';
import DetailsMedia from './sections/DetailsMedia.vue';
import cloneDeep from 'lodash/cloneDeep';
import { showSmallSenateStatistic, showWerberatStatistic } from '@/utils/statisticHandling/statistics';
import moment from 'moment';
import {
	getCallbacksCount,
	getCaseVoteEnde,
	isSmallSenateDecision,
	hasDecision,
	isNotSentToSmallSenateYet,
	isSentToSmallSenate,
	isInStatements,
	isSentToWerberat,
} from '@/utils/casesHandling/cases';

const ElMessageDuration = 3000;

export default defineComponent({
	name: 'FallDetailView',
	data() {
		return {
			data: null as CaseResponse | null,
			initialData: null as CaseResponse | null,
			saveCaseRequest: null as SaveCaseRequest | null,
			formId: 'casesForm',
			rules: {
				title: [{ required: true, message: texts.formErrors.reqMissing, trigger: ['blur'] }],
				sectors: [{ required: true, message: texts.formErrors.reqMissing, trigger: ['blur', 'change'] }],
				category: [{ required: true, message: texts.formErrors.reqMissing, trigger: ['blur', 'change'] }],
				mediums: [{ required: true, message: texts.formErrors.reqMissing, trigger: ['blur', 'change'] }],
				reaches: [{ required: true, message: texts.formErrors.reqMissing, trigger: ['blur', 'change'] }],
			},
			waiting: false,
			loading: true,
			nextSmallSenate: '',
			localTitle: '',
			SendCaseType,
			openDialogRequestStatement: false,
			openDialogPreliminaryDecision: false,
			openDialogSendCaseSmallSenate: false,
			openDialogSendCaseWerberat: false,
			openDialogPublishCase: false,
			CaseState,
			DecisionState,
			DecisionType,
			caseFiles: [] as DetailFile[],
			caseLinks: [] as DetailLink[],
			logs: [] as HistoryEntryResponse[],
			logsLoading: true,
			statistic: null as StatisticResponse | null,
			openClosingDialog: false,
			openDialogeLeavePageCaseErr: false,
			checkPageLeave: true,
			pageLeaveTo: null as RouteLocationRaw | null,
			isEditingFinalDecision: false,
			smallSenateSectionCollapsed: true,
		};
	},
	props: {
		id: {
			type: String,
			required: true,
		},
	},
	components: {
		ButtonBack,
		ButtonSticky,
		TheButton,
		TheForm,
		TheBox,
		DecisionsTable,
		Plus,
		Loading,
		RoleSlider,
		DetailsMedia,
		DetailsLinks,
		saveIcon,
		ComplaintsTable,
		StatementsTable,
		BoxSenateDecision,
		BoxWerberatDecision,
		DialogPreliminaryDecision,
		DialogSendCaseSmallSenate,
		DialogSendCaseWerberat,
		DialogRequestStatement,
		DialogPublishCase,
		DialogLeavePage,
		DialogeLeavePageCaseErr,
		LogsTable,
		ElCollapse,
		ElCollapseItem,
		SectionStatistics,
	},
	setup() {
		const formRef = ref<FormInstance | null>(null);

		return {
			formRef,
		};
	},
	computed: {
		texts() {
			return texts;
		},
		categoryOptions() {
			return returnFormOptions('complaintAndCaseCategories');
		},
		mediaOptions() {
			return returnFormOptions('media');
		},
		sectorOptions() {
			return returnFormOptions('sector');
		},
		reachOptions() {
			return returnFormOptions('reach');
		},
		selectedCategory: {
			get(): string {
				return this.data?.category?.number || '';
			},
			set(value: string) {
				if (this.data) {
					this.data.category = {
						id: '',
						number: value,
						name: '',
					};
				} else return [{ id: '', number: '', name: '' }];
			},
		},
		selectedSectors: {
			get(): string[] {
				return this.data?.sectors?.map((s) => s.name) || [];
			},
			set(value: string[]) {
				if (this.data) {
					this.data.sectors = value.map((s) => {
						return {
							id: '',
							name: s,
						};
					});
				} else return [{ id: '', name: '' }];
			},
		},
		selectedMediums: {
			get(): string[] {
				return this.data?.mediums?.map((m) => m.name) || [];
			},
			set(value: string[]) {
				if (this.data) {
					this.data.mediums = value.map((m) => {
						return {
							id: '',
							name: m,
						};
					});
				} else return [{ id: '', name: '' }];
			},
		},
		selectedReaches: {
			get(): string[] {
				return this.data?.reaches?.map((r) => r.name) || [];
			},
			set(value: string[]) {
				if (this.data) {
					this.data.reaches = value.map((r) => {
						return {
							id: '',
							name: r,
						};
					});
				} else return [{ id: '', name: '' }];
			},
		},
		isStatementAllowed(): boolean {
			if (!this.data) return true;

			return [CaseState.NoStatus, CaseState.ExaminationSmallSenate, CaseState.ExaminationSmallSenateRunning, CaseState.Statements].includes(
				this.data.state,
			);
		},
		smallSenateDecisions() {
			if (!this.data) return [];

			return this.data.decisions.filter((d) => d.decidedDate && d.decisionType === DecisionType.SmallSenate && d.reasonText);
		},
		werberatDecisions() {
			if (!this.data) return [];

			return this.data.decisions.filter(
				(d) => d.decidedDate && (d.decisionType === DecisionType.Werberat || d.decisionType === DecisionType.JungerWerberat) && d.reasonText,
			);
		},
	},
	methods: {
		async saveCase(leavePage = false) {
			if (!this.data) return;

			if (!this.data.publishedDate) {
				const isValid = await validateForm(this.formRef, this.formId);
				if (!isValid) return;

				this.waiting = true;
				const waitingMsg = ElMessage({ message: texts.formErrors.waiting, type: 'info', duration: 0, showClose: false, icon: markRaw(Loading) });

				const saveCaseRequest: SaveCaseRequest = {
					caseId: this.data.id,
					title: this.data.title,
					category: { number: this.data.category.number },
					sectors: this.data.sectors.map((s) => {
						return { name: s.name };
					}),
					mediums: this.data.mediums.map((m) => {
						return { name: m.name };
					}),
					reaches: this.data.reaches.map((r) => {
						return { name: r.name };
					}),
					internalNote: this.data.internalNote,
					externalNote: this.data.externalNote,
				};

				const newLinks = this.caseLinks
					.filter((l) => l.isNew)
					.map((l) => l.link)
					.map((l) => {
						return { name: l.name, url: l.url, type: l.type } as LinkDto;
					});

				if (newLinks.length > 0) saveCaseRequest.links = newLinks;

				const newFiles = this.caseFiles
					.filter((f) => f.isNew)
					.map((f) => f.file)
					.map((f) => {
						return { fileName: f.fileName, contentType: f.contentType, bytes: f.bytes } as FileDto;
					});
				if (newFiles.length > 0) saveCaseRequest.files = newFiles;

				const saveResult = await saveCase(saveCaseRequest);
				if (saveResult.success) {
					if (leavePage) this.leavePage();
					else {
						this.loadCase();

						// setze alle files und links auf isNew = false damit sie nicht doppelt hochgeladen werden
						this.caseFiles = this.caseFiles.map((f) => {
							return { file: f.file, isNew: false };
						});
						this.caseLinks = this.caseLinks.map((l) => {
							return { link: l.link, isNew: false };
						});

						ElMessage({ message: this.texts.cases.saveSuccess, type: 'success', duration: ElMessageDuration });
					}
				} else {
					ElMessage({ message: this.texts.cases.saveFailed, type: 'error', duration: ElMessageDuration });
				}

				this.waiting = false;
				waitingMsg.close();
			} else {
				const saveCaseRequest: SaveCaseRequest = {
					caseId: this.data.id,
					publishedDate: this.data.publishedDate,
				};

				const saveResult = await saveCase(saveCaseRequest);
				if (saveResult.success) {
					this.loadCase();
					ElMessage({ message: this.texts.cases.saveSuccess, type: 'success', duration: ElMessageDuration });
				} else {
					ElMessage({ message: this.texts.cases.saveFailed, type: 'error', duration: ElMessageDuration });
				}
			}
		},
		async loadCase() {
			const loading = startLoading('#fall');

			const result = await getCase(this.id);
			if (result.success) {
				this.data = result.data;
				this.initialData = cloneDeep(result.data);

				const seenUsers = new Set();
				const duplicateDecisions: any[] = [];

				this.data.decisions.forEach((decision) => {
					const userId = decision.user.id;
					if (seenUsers.has(userId)) {
						duplicateDecisions.push(decision);
					} else {
						seenUsers.add(userId);
					}
				});

				this.localTitle = this.data.title;
				this.caseFiles = getCaseFiles(this.data);
				this.caseLinks = getCaseLinks(this.data);

				this.loadLogs();
			}

			this.loading = false;
			stopLoading(loading);
		},
		async loadStatistics() {
			const result = await getCaseStatistics(this.id);
			if (result.success) {
				this.statistic = result.data;
			}
		},
		async loadLogs() {
			this.logsLoading = true;
			const result = await getCaseLogs(this.id);
			if (result.success) {
				this.logs = result.data;
			}
			this.logsLoading = false;
		},
		addToFiles(file) {
			this.caseFiles.push(file);
		},
		removeFromFiles(file) {
			this.caseFiles = this.caseFiles.filter((f) => f.file !== file.file);
		},
		removeFromLinks(link) {
			this.caseLinks = this.caseLinks.filter((l) => l.link !== link.link);
		},
		addToLinks(link) {
			this.caseLinks.push(link);
		},
		async handleOpenSmallSenateDialog() {
			const nextSmallsenateResult = await getCaseNextSmallSenate();
			if (nextSmallsenateResult.success) {
				this.nextSmallSenate = nextSmallsenateResult.data.name;
				this.openDialogSendCaseSmallSenate = true;
			} else {
				ElMessage({ message: this.texts.cases.smallSenateGetterFailed, type: 'error', duration: ElMessageDuration });
			}
		},
		async closeDialogSendCaseSmallSenate(reload = false) {
			if (reload) {
				await this.loadCase();
			}
			this.nextSmallSenate = '';
		},
		async closeDialogSendCaseWerberat(reload = false) {
			if (reload) {
				await this.loadCase();
			}
			this.nextSmallSenate = '';
		},

		leavePage() {
			this.openClosingDialog = false;
			this.openDialogeLeavePageCaseErr = false;
			this.checkPageLeave = false;

			// @ts-ignore
			this.$router.push(this.pageLeaveTo);
		},
		handleBeforeRouteLeave(to, from, next) {
			if (this.checkPageLeave === false) {
				next();
				return;
			}

			// if values dont exist: data could not be loaded
			// if publishedDate: case is already published
			if (!this.initialData || !this.data || this.data.publishedDate) {
				next();
				return;
			}

			// abort navigation when Branche or Reichweite is empty
			if (this.selectedSectors.length === 0 || this.selectedReaches.length === 0) {
				// ABORT NAVIGATION & SHOW DIALOG
				validateForm(this.formRef, this.formId);

				this.openDialogeLeavePageCaseErr = true;
				this.pageLeaveTo = to;
				next(false);
				return;
			}

			// check if data has changed
			const hasChanged = hasFormChanged(
				{ initialData: this.initialData, currentData: this.data },
				this.caseFiles.filter((f) => f.isNew),
				this.caseLinks.filter((l) => l.isNew),
			);

			if (!hasChanged) {
				next();
				return;
			}

			// ABORT NAVIGATION & SHOW DIALOG
			this.openClosingDialog = true;
			this.pageLeaveTo = to;
			next(false);
		},
		getCallbacksCount,
		getCaseVoteEnde,
		isSmallSenateDecision,
		hasDecision,
		isNotSentToSmallSenateYet,
		isSentToSmallSenate,
		isInStatements,
		isSentToWerberat,
		showSmallSenateStatistic,
		showWerberatStatistic,
	},
	mounted() {
		this.loadCase();
		this.loadStatistics();
	},
	watch: {
		'data.publishedDate': {
			handler() {
				if (this.data?.publishedDate) {
					this.data.publishedDate = moment(this.data.publishedDate).toISOString(true);
				}
			},
			immediate: true,
		},
	},
});
</script>

<style lang="scss">
#decision-final {
	.has-status {
		margin-bottom: 2rem;
	}
}
</style>
